import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CorrelationsService } from '../../services/correlationsService';
import { Correlation, CorrelationDetail, CorrelationsState } from '../../types';

const initialState: CorrelationsState = {
  correlations: [],
  selectedCorrelation: null,
  isLoading: false,
  isDetailLoading: false,
  error: null,
  total: 0,
};

export const fetchCorrelations = createAsyncThunk(
  'correlations/fetchCorrelations',
  async (
    params: { status?: string; client_id?: string; limit?: number; offset?: number } | undefined,
    { rejectWithValue },
  ) => {
    try {
      return await CorrelationsService.getCorrelations(params);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch correlations');
    }
  },
);

export const fetchCorrelationById = createAsyncThunk(
  'correlations/fetchCorrelationById',
  async (correlationId: string, { rejectWithValue }) => {
    try {
      return await CorrelationsService.getCorrelationById(correlationId);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch correlation');
    }
  },
);

export const updateCorrelationStatus = createAsyncThunk(
  'correlations/updateStatus',
  async (
    { id, status }: { id: string; status: 'open' | 'dismissed' | 'escalated' },
    { rejectWithValue },
  ) => {
    try {
      return await CorrelationsService.updateStatus(id, status);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update correlation');
    }
  },
);

const correlationsSlice = createSlice({
  name: 'correlations',
  initialState,
  reducers: {
    clearSelectedCorrelation: (state) => {
      state.selectedCorrelation = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCorrelations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCorrelations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.correlations = action.payload.correlations ?? [];
        state.total = action.payload.total ?? 0;
      })
      .addCase(fetchCorrelations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCorrelationById.pending, (state) => {
        state.isDetailLoading = true;
      })
      .addCase(fetchCorrelationById.fulfilled, (state, action) => {
        state.isDetailLoading = false;
        state.selectedCorrelation = action.payload;
      })
      .addCase(fetchCorrelationById.rejected, (state, action) => {
        state.isDetailLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateCorrelationStatus.fulfilled, (state, action) => {
        const updated = action.payload as Correlation;
        const idx = state.correlations.findIndex((c) => c.id === updated.id);
        if (idx !== -1) state.correlations[idx] = updated;
        if (state.selectedCorrelation?.id === updated.id) {
          state.selectedCorrelation = { ...state.selectedCorrelation, ...updated };
        }
      });
  },
});

export const { clearSelectedCorrelation } = correlationsSlice.actions;
export default correlationsSlice.reducer;